<template lang="pug">
  SupplierLayout
    CommonSection.b-supplier-match-page(title="Проверка сопоставления КП" data-testid="supplier-match-page")
      ._layout
        ._preview(data-testid="supplier-match-preview")
          ._file-name {{ fileName }}
          VuePdfEmbed(v-if="isPdf" :source="fileUrl")
          img._image(v-else-if="isImage" :src="fileUrl" :alt="fileName")
          pre._text(v-else-if="preparedText") {{ preparedText }}
          ._no-preview(v-else)
            | Документ КП открывается по ссылке: встроенный просмотр есть у PDF и изображений.
            a._download(v-if="fileUrl" :href="fileUrl" target="_blank" rel="noopener noreferrer") {{ fileName }}

        ._rows
          ._status(v-if="statusText" data-testid="supplier-match-status") {{ statusText }}
          ._empty(v-if="ready && !rows.length") Строк в КП не найдено
          table._table(v-if="rows.length")
            thead
              tr
                th Строка КП
                th Кол-во
                th Цена
                th Скидка
                th Срок
                th Замена
                th Комментарий
                th Позиция запроса
                th Совпадение
                th Уверенность
                th
            tbody
              tr._row(v-for="(row, index) in rows" :key="index" data-testid="supplier-match-row")
                td
                  span._name(data-testid="supplier-match-row-name") {{ row.name }}
                  span._manual(v-if="row.manual" data-testid="supplier-match-row-manual")
                    | строки в КП нет — отклик вручную
                td
                  input._input.narrow(data-testid="supplier-match-row-quantity" v-model="row.quantity")
                  span._warning(
                    v-if="quantityMismatch(row)"
                    data-testid="supplier-match-row-quantity-warning"
                    ) запрошено {{ itemQuantity(row) }}
                  span._error(
                    v-if="quantityInvalid(row)"
                    data-testid="supplier-match-row-quantity-error"
                    ) нужно число больше нуля
                td
                  input._input.narrow(data-testid="supplier-match-row-price" v-model="row.price")
                  span._error(
                    v-if="priceMissing(row)"
                    data-testid="supplier-match-row-price-error"
                    ) нужна цена
                td
                  select._select(data-testid="supplier-match-row-discount-type" v-model="row.discountType")
                    option(
                      v-for="(title, code) in DISCOUNT_TYPES"
                      :key="code"
                      :value="code"
                      data-testid="supplier-match-row-discount-type-option"
                      ) {{ title }}
                  input._input.narrow(data-testid="supplier-match-row-discount-value" v-model="row.discountValue")
                  span._error(
                    v-if="discountError(row)"
                    data-testid="supplier-match-row-discount-error"
                    ) {{ discountErrorText(row) }}
                td._delivery
                  button._delivery-button(
                    type="button"
                    data-testid="supplier-match-row-delivery"
                    @click="editorIndex = index"
                    ) {{ deliveryText(row.deliveryTerm, 'указать срок') }}
                  SpecDeliveryEditor(
                    v-if="editorIndex === index"
                    :value="row.deliveryTerm"
                    @save="saveDelivery(row, $event)"
                    @close="editorIndex = null"
                    )
                td
                  input._input(
                    data-testid="supplier-match-row-replacement-text"
                    v-model="row.replacementText"
                    )
                td
                  input._input(data-testid="supplier-match-row-comment" v-model="row.comment")
                td
                  select._select(data-testid="supplier-match-row-item" :value="row.itemId || ''" @change="bind(row, $event.target.value)")
                    option(value="" data-testid="supplier-match-row-item-option") нет пары
                    option(
                      v-for="option in itemOptions"
                      :key="option.id"
                      :value="String(option.id)"
                      data-testid="supplier-match-row-item-option"
                      ) {{ option.name }}
                td
                  span._type(data-testid="supplier-match-row-type") {{ typeText(row) }}
                td
                  span._confidence.high(
                    v-if="row.confidence === 'high'"
                    data-testid="supplier-match-row-confidence-high"
                    title="высокая уверенность"
                    ) ●
                  span._confidence.medium(
                    v-else-if="row.confidence === 'medium'"
                    data-testid="supplier-match-row-confidence-medium"
                    title="средняя уверенность"
                    ) ●
                  span._confidence.low(
                    v-else-if="row.confidence === 'low'"
                    data-testid="supplier-match-row-confidence-low"
                    title="низкая уверенность"
                    ) ●
                td
                  button._delete(type="button" data-testid="supplier-match-row-delete" @click="remove(index)") ×

          ._unmatched(v-if="unmatched.length" data-testid="supplier-match-unmatched")
            ._unmatched-title Позиции запроса без пары
            ._unmatched-item(
              v-for="option in unmatched"
              :key="option.id"
              data-testid="supplier-match-unmatched-item"
              )
              span._unmatched-name(data-testid="supplier-match-unmatched-name") {{ option.name }}
              Btn(
                grey
                xs
                data-testid="supplier-match-unmatched-add"
                @click="addManual(option)"
                ) Добавить отклик вручную

          ._buttons
            Btn(
              blue
              data-testid="supplier-match-submit"
              :disabled="!canSubmit"
              @click="submit"
              ) Сформировать отклики ({{ boundRows.length }})
            Btn(grey data-testid="supplier-match-cancel" @click="back") Отмена
</template>

<script>
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
import SupplierLayout from '@/layouts/Supplier.vue';
import CommonSection from '@/components/CommonSection.vue';
import SpecDeliveryEditor from '@/components/specification/SpecDeliveryEditor.vue';
import model, { POLL_INTERVAL, JOB_ACTIVE } from '@/model/supplierCabinet';
import {
  DISCOUNT_TYPES, MATCH_TYPES, deliveryText, toNumber,
} from '@/utils/spec';

/**
 * Экран проверки сопоставления КП (ТЗ 6.5, 5.5): слева документ КП (PDF и изображения —
 * встроенным просмотрщиком, таблицы и docx — подготовленным текстом из результата задания, как на
 * экране проверки импорта), справа строки КП с
 * извлечёнными полями. Наименование строки показывается как есть — по нему поставщик сверяет строку
 * с позицией; количество, цена, скидка, срок, текст замены и комментарий правятся в строке и
 * переносятся в черновик отклика. Количество, отличное от запрошенного, помечается; пустое
 * количество сервер заменит запрошенным. Скидка больше цены подсвечена ошибкой и не даёт сформировать
 * отклики (2.8 — то же правило проверяет сервер).
 *
 * Привязка строки к позиции запроса меняется списком, «нет пары» отвязывает; позиции без пары
 * собраны отдельным списком, и «Добавить отклик вручную» добавляет по такой позиции строку прямо
 * здесь — правки экрана при этом не теряются. Кнопка «Сформировать отклики (N)» считает строки с
 * привязанной позицией. Сгенерировано Claude.
 */
export default {
  name: 'SupplierMatchPage',
  components: {
    SupplierLayout, CommonSection, SpecDeliveryEditor, VuePdfEmbed,
  },
  data() {
    return {
      DISCOUNT_TYPES,
      quoteFile: null,
      job: null,
      card: null,
      rows: [],
      editorIndex: null,
      timer: null,
      busy: false,
    };
  },
  computed: {
    requestId() { return Number(this.$route.params.requestId); },
    quoteFileId() { return Number(this.$route.params.quoteFileId); },
    file() { return this.quoteFile?.file ?? null; },
    fileName() { return this.file?.name ?? 'Файл КП'; },
    fileUrl() { return this.file?.url ?? null; },
    isPdf() {
      return (this.file?.extension ?? '').toLowerCase() === 'pdf'
        || this.file?.type === 'application/pdf';
    },
    isImage() { return String(this.file?.type ?? '').startsWith('image/'); },
    /**
     * Подготовленный текст документа КП из результата задания — тем же составом, что у импорта
     * (ТЗ 5.2, 6.5): у PDF и изображений он пуст, их показывает просмотрщик.
     */
    preparedText() {
      const sources = this.job?.result?.sources ?? [];
      const source = sources.find((s) => Number(s.fileId) === Number(this.file?.id));
      return source?.preparedText || null;
    },
    ready() { return this.job?.status === 'done'; },
    statusText() {
      if (!this.job) return '';
      if (JOB_ACTIVE.includes(this.job.status)) return 'сопоставление: в обработке';
      if (this.job.status === 'error') return `ошибка: ${this.job.error || 'не удалось распознать файл'}`;
      if (this.job.status === 'cancelled') return 'сопоставление отменено';
      return '';
    },
    /** Позиции запроса — варианты привязки строки КП (ТЗ 6.5). */
    itemOptions() {
      return (this.card?.items ?? [])
        .filter((row) => row.item)
        .map((row) => ({ id: row.item.id, name: row.item.name, quantity: row.item.quantity }));
    },
    boundRows() { return this.rows.filter((row) => !!row.itemId); },
    unmatched() {
      const bound = new Set(this.boundRows.map((row) => Number(row.itemId)));
      return this.itemOptions.filter((option) => !bound.has(Number(option.id)));
    },
    canSubmit() {
      if (this.busy || !this.boundRows.length) return false;
      return !this.boundRows.some((row) => this.discountError(row) || this.quantityInvalid(row)
        || toNumber(row.price) === null);
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    deliveryText,

    async load() {
      this.quoteFile = await model.fetchQuoteFile(this.quoteFileId);
      this.card = await model.fetchCard(this.requestId, false);
      await this.loadJob();
    },

    async loadJob() {
      const jobId = this.quoteFile?.aiJob?.id;
      if (!jobId) return;
      this.job = await model.fetchJob(jobId);
      if (JOB_ACTIVE.includes(this.job.status)) {
        this.startPolling();
        return;
      }
      this.stopPolling();
      this.fillRows();
    },

    startPolling() {
      if (this.timer) return;
      this.timer = setInterval(() => { this.loadJob(); }, POLL_INTERVAL);
    },
    stopPolling() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },

    /** Строки результата сопоставления в правимый черновик (ТЗ 5.5). */
    fillRows() {
      this.rows = (this.job?.result?.rows ?? []).map((row) => ({
        name: row.name ?? '',
        quantity: row.quantity ?? '',
        price: row.price ?? '',
        discountType: row.discount?.type ?? 'percent',
        discountValue: row.discount?.value ?? '0',
        deliveryTerm: row.availability ?? null,
        isReplacement: row.matchType === 'replacement',
        replacementText: row.replacementText ?? '',
        comment: row.comment ?? '',
        itemId: row.itemId ?? null,
        confidence: row.confidence ?? 'medium',
      }));
    },

    itemOf(row) {
      return this.itemOptions.find((option) => Number(option.id) === Number(row.itemId)) ?? null;
    },
    itemQuantity(row) {
      const item = this.itemOf(row);
      return item ? (toNumber(item.quantity) ?? '') : '';
    },
    /** Расхождение количества строки КП и позиции — предупреждение в строке (ТЗ 6.5). */
    quantityMismatch(row) {
      const item = this.itemOf(row);
      const inQuote = toNumber(row.quantity);
      const inItem = item ? toNumber(item.quantity) : null;
      return item !== null && inQuote !== null && inItem !== null && inQuote !== inItem;
    },
    /** Количество можно оставить пустым (тогда — запрошенное), но не нулём и не текстом. */
    quantityInvalid(row) {
      if (String(row.quantity ?? '').trim() === '') return false;
      const value = toNumber(row.quantity);
      return value === null || value <= 0;
    },
    /** Без цены отклик не сформировать: у привязанной строки цена обязательна (ТЗ 2.6, 6.9). */
    priceMissing(row) {
      return !!row.itemId && toNumber(row.price) === null;
    },
    /** Скидка, с которой отклик сформировать нельзя (ТЗ 2.8): то же правило, что на сервере. */
    discountError(row) {
      return !!this.discountErrorText(row);
    },
    /** Причина называется своими словами: пустое поле — не «скидка больше цены» (ТЗ 6.9). */
    discountErrorText(row) {
      const value = toNumber(row.discountValue);
      const price = toNumber(row.price);
      if (value === null) return 'нужно число';
      if (value < 0) return 'скидка не может быть отрицательной';
      if (row.discountType === 'percent') {
        return value > 100 ? 'скидка больше 100 %' : '';
      }
      if (price === null) return 'сначала укажите цену';
      return value > price ? 'скидка больше цены' : '';
    },
    /** (Claude, ревью этапа 8) Замена — по результату сопоставления или по введённому в строке тексту замены (ТЗ 6.5). */
    isReplacementRow(row) {
      return row.isReplacement || !!String(row.replacementText ?? '').trim();
    },
    typeText(row) {
      if (!row.itemId) return MATCH_TYPES.none;
      return this.isReplacementRow(row) ? MATCH_TYPES.replacement : MATCH_TYPES.match;
    },
    bind(row, value) {
      row.itemId = value ? Number(value) : null; // eslint-disable-line no-param-reassign
    },
    saveDelivery(row, term) {
      row.deliveryTerm = term; // eslint-disable-line no-param-reassign
      this.editorIndex = null;
    },
    remove(index) {
      this.rows = this.rows.filter((row, i) => i !== index);
      this.editorIndex = null;
    },
    /**
     * «Добавить отклик вручную» по позиции без пары (ТЗ 6.5): строка добавляется здесь же, в рамках
     * проверки сопоставления, и уходит в отклики вместе с остальными по кнопке «Сформировать
     * отклики». Переход в карточку запроса за формой отклика здесь не годится: строки КП — состояние
     * этого экрана, и уход с него потерял бы уже сделанные правки цен, скидок, сроков и привязок.
     */
    addManual(option) {
      this.rows = [...this.rows, {
        name: option.name,
        quantity: String(toNumber(option.quantity) ?? ''),
        price: '',
        discountType: 'percent',
        discountValue: '0',
        deliveryTerm: null,
        isReplacement: false,
        replacementText: '',
        comment: '',
        itemId: option.id,
        // уверенности у ручной строки нет: её не распознавали (ТЗ 5.5)
        confidence: null,
        manual: true,
      }];
    },

    async submit() {
      if (!this.canSubmit) return;
      this.busy = true;
      const payload = this.boundRows.map((row) => ({
        itemId: Number(row.itemId),
        quantity: toNumber(row.quantity) === null ? null : String(toNumber(row.quantity)),
        price: String(toNumber(row.price)),
        discount: { type: row.discountType, value: String(toNumber(row.discountValue) ?? 0) },
        availability: row.deliveryTerm,
        isReplacement: this.isReplacementRow(row),
        replacementText: this.isReplacementRow(row) ? (row.replacementText || null) : null,
        comment: row.comment || null,
      }));

      const applied = await model.applyMatch(this.job.id, payload);
      this.busy = false;
      if (!applied) return;
      this.back();
    },

    back() {
      this.$router.push({
        name: 'supplier-request',
        params: { requestId: String(this.requestId) },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.supplier-match-page {
  &__layout {
    display: flex;
    gap: 20px;
    align-items: flex-start;
  }

  &__preview {
    width: 360px;
    flex: 0 0 360px;
    max-height: 75vh;
    overflow: auto;
    padding: 12px;
    box-sizing: border-box;
    border: 1px solid var(--color-super-light);
    border-radius: 8px;
    background: var(--color-white);
  }

  &__file-name {
    display: block;
    font-size: 12px;
    color: var(--color-middle-gray);
    margin-bottom: 6px;
  }

  &__image {
    max-width: 100%;
  }

  // подготовленный текст документа КП для таблиц и docx (ТЗ 5.2, 6.5)
  &__text {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 11px;
    font-family: inherit;
    margin: 0;
  }

  &__no-preview {
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__download {
    display: block;
    margin-top: 6px;
    color: var(--color-accent);
  }

  &__rows {
    flex: 1 1 0;
    min-width: 0;
    overflow-x: auto;
  }

  &__status {
    margin-bottom: 10px;
    font-size: 13px;
    color: var(--color-middle-gray);
  }

  &__empty {
    font-size: 13px;
    color: var(--color-middle-gray);
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;

    th,
    td {
      text-align: left;
      padding: 4px 6px;
      border-bottom: 1px solid var(--color-super-light);
      vertical-align: top;
    }

    th {
      color: var(--color-middle-gray);
      font-weight: 400;
      white-space: nowrap;
    }
  }

  &__delivery {
    position: relative;
  }

  &__delivery-button {
    box-sizing: border-box;
    min-width: 110px;
    padding: 3px 5px;
    border: 1px solid var(--color-super-light);
    border-radius: 4px;
    background: var(--color-white);
    font: inherit;
    font-size: 12px;
    text-align: left;
    cursor: pointer;
  }

  &__input,
  &__select {
    box-sizing: border-box;
    width: 100%;
    min-width: 100px;
    padding: 3px 5px;
    border: 1px solid var(--color-super-light);
    border-radius: 4px;
    font: inherit;
    font-size: 12px;
  }

  &__input.narrow {
    min-width: 70px;
  }

  &__warning {
    display: block;
    font-size: 11px;
    color: var(--color-orange);
  }

  // строка, добавленная вручную по позиции без пары (ТЗ 6.5)
  &__manual {
    display: block;
    font-size: 11px;
    color: var(--color-middle-gray);
  }

  &__error {
    display: block;
    font-size: 11px;
    color: var(--color-red);
  }

  // уверенность распознавания цветом (ТЗ 6.5, 5.5)
  &__confidence {
    font-size: 16px;
    line-height: 1;

    &.high { color: var(--color-green); }
    &.medium { color: var(--color-yellow); }
    &.low { color: var(--color-red); }
  }

  &__delete {
    border: none;
    background: none;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
  }

  &__unmatched {
    margin-top: 16px;
    padding: 10px 12px;
    border: 1px solid var(--color-super-light);
    border-radius: 8px;
    background: var(--color-white);
  }

  &__unmatched-title {
    font-weight: 700;
    font-size: 13px;
    margin-bottom: 6px;
  }

  &__unmatched-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 3px 0;
    font-size: 13px;
  }

  &__buttons {
    display: flex;
    gap: 12px;
    margin-top: 16px;
  }

  // Узкие экраны (ТЗ 6.6, 6.10 — общий порог модуля 768 px): просмотр документа КП перестаёт быть
  // колонкой фиксированной ширины и уходит над строками, а таблица строк прокручивается по
  // горизонтали внутри своего блока, а не растягивает страницу
  @media (max-width: 768px) {
    &__layout {
      flex-direction: column;
    }

    &__preview {
      width: 100%;
      flex: 0 0 auto;
      max-height: 40vh;
    }

    &__rows {
      width: 100%;
    }

    &__table {
      width: auto;
      min-width: 720px;
    }

    &__buttons {
      flex-wrap: wrap;
    }
  }
}
</style>
